import axios from 'axios'
import { resolveElapsedSeconds } from '@/utils/examTiming'

export function guestExamSidKey(examId) {
  return `h5_exam_sid_${examId}`
}

function pendingAnswersKey(examId) {
  return `guest_exam_pending_answers_${examId}`
}

/** 提交前缓存答案，登录认领前可再次写入 Redis（防止会话过期或仅保存在本地 sid） */
export function stashPendingGuestAnswers(examId, answersJson) {
  if (examId && answersJson) {
    localStorage.setItem(pendingAnswersKey(examId), answersJson)
  }
}

export function clearPendingGuestAnswers(examId) {
  if (examId) {
    localStorage.removeItem(pendingAnswersKey(examId))
  }
}

/** 认领前把本地缓存的答案同步到匿名会话 */
export async function syncPendingGuestAnswersBeforeClaim(examId, sid) {
  if (!examId || !sid) {
    return sid
  }
  const answersJson = localStorage.getItem(pendingAnswersKey(examId))
  if (!answersJson) {
    return sid
  }
  const totalTime = resolveElapsedSeconds(examId)
  return saveGuestAnswers(examId, answersJson, totalTime)
}

export function getStoredSid(examId) {
  return localStorage.getItem(guestExamSidKey(examId)) || ''
}

export function clearStoredSid(examId) {
  localStorage.removeItem(guestExamSidKey(examId))
}

export function clearAllGuestExamSids() {
  const keys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith('h5_exam_sid_')) {
      keys.push(key)
    }
  }
  keys.forEach((key) => localStorage.removeItem(key))
}

function isSidExpiredResponse(resp) {
  const code = resp?.data?.code
  const message = resp?.data?.message || ''
  return code === 410 || message.includes('sid expired')
}

async function postGuestAnswers(sid, examId, answersJson, totalTime = 0) {
  const payload = {
    sid,
    examId: Number(examId),
    answers: answersJson
  }
  if (totalTime > 0) {
    payload.totalTime = totalTime
  }
  return axios.post('/api/v1/h5/exam/answer', payload)
}

export async function ensureGuestSid(examId) {
  const existing = getStoredSid(examId)
  if (existing) return existing

  const resp = await axios.get('/api/v1/h5/exam/start', {
    params: { examId: Number(examId) }
  })
  if (resp.data.code !== 200) {
    throw new Error(resp.data.message || '开始测评失败')
  }
  const sid = resp.data.data.sid
  localStorage.setItem(guestExamSidKey(examId), sid)
  return sid
}

export async function saveGuestAnswers(examId, answersJson, totalTime = 0) {
  let sid = await ensureGuestSid(examId)
  let resp = await postGuestAnswers(sid, examId, answersJson, totalTime)
  if (isSidExpiredResponse(resp)) {
    clearStoredSid(examId)
    sid = await ensureGuestSid(examId)
    resp = await postGuestAnswers(sid, examId, answersJson, totalTime)
  }
  if (resp.data.code !== 200) {
    throw new Error(resp.data.message || '保存失败')
  }
  return sid
}

export async function claimGuestSession(sid, totalTime = 0) {
  const token = localStorage.getItem('jwt_token')
  if (!token) {
    throw new Error('请先登录')
  }
  const body = { sid }
  if (totalTime > 0) {
    body.totalTime = totalTime
  }
  const resp = await axios.post('/api/v1/h5/exam/claim', body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  })
  if (resp.data.code !== 200) {
    const message = resp.data.info
      ? `${resp.data.message || '生成报告失败'}：${resp.data.info}`
      : (resp.data.message || '生成报告失败')
    if (resp.data.code === 410 || message.includes('sid expired') || message.includes('会话已过期')) {
      const err = new Error(message)
      err.code = 'sid_expired'
      throw err
    }
    if (resp.data.code === 403 || message.includes('未购买')) {
      const err = new Error(message)
      err.code = 'not_purchased'
      throw err
    }
    throw new Error(message)
  }
  return resp.data.data
}

/** 未登录时跳转登录页（带 sid/examId，登录后生成报告） */
export function redirectToLoginForClaim({ sid, examId }) {
  return {
    path: '/login',
    query: { sid, examId: String(examId) }
  }
}
